/**
 * Aivy Assistant Intro – "Karl Klammer → Aivy Assistent"
 *
 * Framework-agnostische, zeitbasierte Animation (SVG + HTML).
 * Jeder Frame ist eine reine Funktion von t (Sekunden) → scrubbar, deterministisch.
 *
 *   const intro = createAivyAssistantIntro(el, { background: "#F6F7F9" });
 *   intro.replay(); intro.seek(4.2); intro.pause(); intro.destroy();
 */

import {
  C,
  E,
  MODERN,
  N,
  RETRO,
  clamp,
  lerp,
  mixColor,
  prog,
  rnd,
  spring,
  step,
  toPath,
  xf
} from './clip-geometry'

export const DURATION = 10
const W = 960
const H = 540

const RETRO_POS = { x: 800, y: 382, s: 0.85 }
const MODERN_POS = { x: 228, y: 296, s: 1.6 }
const LINE = { x: 480, y: 270, len: 420 }

// ---------- Copy ----------
const MSG = [
  {
    t: 'Hi! 3 Kandidat:innen haben heute ihr Assessment abgeschlossen. Lena passt zu '
  },
  { t: '92 %', b: true },
  { t: ' auf euer Sales-Profil.' }
]
const MSG_LEN = MSG.reduce((s, m) => s + m.t.length, 0)
const CHIPS = ['Top-Matches zeigen', 'Ergebnisse erklären', 'Interviewfragen']
const ROWS = [
  { n: 'Lena K.', r: 'Sales Lead', v: 92, c: C.green },
  { n: 'Jonas M.', r: 'Sales Lead', v: 87, c: C.yellow },
  { n: 'Aylin S.', r: 'Sales Lead', v: 81, c: C.pink }
]
const DOC_LINES = [94, 88, 97, 72, 0, 91, 84, 95, 60]

const SHAPES = [
  { k: 'tri', c: C.yellow, x: 104, y: 168, s: 26, r: -12 },
  { k: 'sq', c: C.green, x: 352, y: 150, s: 18, r: 18 },
  { k: 'dot', c: C.pink, x: 112, y: 438, s: 10 },
  { k: 'tri', c: C.blue, x: 356, y: 452, s: 16, r: 30 },
  { k: 'dot', c: C.yellow, x: 70, y: 300, s: 6 },
  { k: 'sq', c: C.pink, x: 384, y: 300, s: 10, r: 40 }
]

// ---------- CSS ----------
const CSS = `
.aai-root{position:relative;width:100%;aspect-ratio:16/9;overflow:hidden;border-radius:inherit;user-select:none;-webkit-user-select:none}
.aai-stage{position:absolute;left:0;top:0;width:${W}px;height:${H}px;transform-origin:0 0}
.aai-stage *{box-sizing:border-box}
.aai-abs{position:absolute;left:0;top:0}
.aai-bg-retro{position:absolute;inset:0;background:${C.teal}}
.aai-svg{position:absolute;inset:0;width:${W}px;height:${H}px;overflow:visible}
.aai-win{position:absolute;left:70px;top:62px;width:560px;height:410px;background:#c0c0c0;padding:3px;
  box-shadow:inset -1px -1px #0a0a0a,inset 1px 1px #dfdfdf,inset -2px -2px #808080,inset 2px 2px #fff;
  font-family:Tahoma,Verdana,Geneva,sans-serif;font-size:12px;color:#000;-webkit-font-smoothing:none}
.aai-win-title{height:20px;background:#000080;color:#fff;font-weight:700;display:flex;align-items:center;justify-content:space-between;padding:0 3px 0 6px}
.aai-win-btns{display:flex;gap:2px}
.aai-win-btns i{font-style:normal;width:16px;height:14px;background:#c0c0c0;color:#000;font-size:10px;line-height:12px;text-align:center;
  box-shadow:inset -1px -1px #0a0a0a,inset 1px 1px #fff,inset -2px -2px #808080}
.aai-win-menu{height:22px;display:flex;gap:14px;align-items:center;padding:0 8px}
.aai-win-page{position:absolute;left:10px;right:10px;top:52px;bottom:10px;background:#fff;padding:26px 34px;
  box-shadow:inset 1px 1px #808080,inset -1px -1px #fff,inset 2px 2px #0a0a0a}
.aai-doc-h{font-family:"Times New Roman",Times,serif;font-size:19px;font-weight:700;margin-bottom:16px}
.aai-doc-line{height:9px;background:#9a9a9a;margin-bottom:13px;width:0}
.aai-caret{position:absolute;width:2px;height:15px;background:#000}
.aai-crt{position:absolute;inset:0;background:#fff;opacity:0}
.aai-bubble{position:absolute;left:642px;top:58px;width:298px;background:#FFFFE1;border:1px solid #000;border-radius:10px;
  padding:12px 14px 12px;font-family:Tahoma,Verdana,Geneva,sans-serif;font-size:12.5px;line-height:1.35;color:#000;-webkit-font-smoothing:none;transform-origin:70% 100%}
.aai-bubble-tail{position:absolute;left:146px;bottom:-25px;width:24px;height:26px}
.aai-bubble p{margin:0 0 9px}
.aai-opt{display:flex;gap:7px;align-items:center;padding:2px 4px;margin:0 -4px 2px;border-radius:2px}
.aai-opt b{width:9px;height:9px;border-radius:50%;background:#0000ff;flex:none}
.aai-opt.hover span{color:#0000ff;text-decoration:underline}
.aai-opt.down{background:#000080}.aai-opt.down span{color:#fff;text-decoration:none}
.aai-check{display:flex;gap:7px;align-items:center;margin-top:8px;color:#333}
.aai-check i{width:12px;height:12px;background:#fff;border:1px solid #000}
.aai-cursor{position:absolute;left:0;top:0;width:22px;height:30px;transform-origin:2px 2px}
.aai-label{position:absolute;left:22px;top:18px;height:32px;display:flex;align-items:center;gap:10px;padding:0 14px 0 12px;border-radius:16px;
  background:${C.dark};color:#fff;font-family:"Fira Sans",system-ui,sans-serif;font-size:14px;font-weight:500;white-space:nowrap}
.aai-odo{display:flex;font-weight:600;font-variant-numeric:tabular-nums}
.aai-col{display:block;height:20px;width:0.62em;overflow:hidden;position:relative}
.aai-strip{display:flex;flex-direction:column;position:absolute;left:0;top:0;width:100%}
.aai-strip span{height:20px;line-height:20px;text-align:center}
.aai-sep{width:4px;height:4px;border-radius:50%;background:${C.grey}}
.aai-names{display:grid;height:20px;overflow:hidden}
.aai-names span{grid-area:1/1;line-height:20px}
.aai-card{position:absolute;left:404px;top:96px;width:500px;background:#fff;border:1px solid #E3E6EC;border-radius:20px;
  padding:18px 22px;font-family:Inter,system-ui,sans-serif;color:${C.dark};opacity:0}
.aai-head{display:flex;align-items:center;gap:10px;padding-bottom:14px;border-bottom:1px solid #EEF0F3}
.aai-ava{width:34px;height:34px;border-radius:50%;background:${C.blue};display:flex;align-items:center;justify-content:center;flex:none}
.aai-head-t{font-family:"Fira Sans",system-ui,sans-serif;font-weight:600;font-size:16px;line-height:1.2}
.aai-head-s{font-size:12px;color:#6b6f76;display:flex;align-items:center;gap:6px;line-height:1.3}
.aai-head-s i{width:7px;height:7px;border-radius:50%;background:${C.green}}
.aai-msg{position:relative;margin-top:16px;background:${C.coolWhite};border-radius:4px 16px 16px 16px;padding:13px 16px;font-size:15px;line-height:1.5;opacity:0;transform-origin:0 0}
.aai-msg b{color:${C.blue};font-weight:600}
.aai-msg .aai-ghost{opacity:0}
.aai-tcaret{display:inline-block;width:2px;height:16px;background:${C.blue};vertical-align:-3px;margin-left:1px}
.aai-dots{position:absolute;left:16px;top:20px;display:flex;gap:5px}
.aai-dots i{width:7px;height:7px;border-radius:50%;background:${C.grey}}
.aai-chips{display:flex;gap:8px;margin-top:14px}
.aai-chip{position:relative;overflow:hidden;height:34px;padding:0 14px;display:flex;align-items:center;border:1.5px solid ${C.blue};color:${C.blue};
  border-radius:17px;font-size:13.5px;font-weight:500;white-space:nowrap;opacity:0;background:#fff}
.aai-chip.on{background:${C.blue};color:#fff}
.aai-ripple{position:absolute;width:10px;height:10px;border-radius:50%;background:#fff;left:0;top:0;opacity:0;pointer-events:none}
.aai-rows{margin-top:16px;display:flex;flex-direction:column;gap:10px}
.aai-row{display:flex;align-items:center;gap:12px;opacity:0;height:36px}
.aai-rava{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:${C.dark};flex:none}
.aai-rname{width:96px;font-size:14px;font-weight:500;line-height:1.25}
.aai-rname small{display:block;font-size:11.5px;font-weight:400;color:#80858c}
.aai-track{flex:1;height:8px;border-radius:4px;background:#EDEFF3;overflow:hidden}
.aai-fill{height:100%;width:0;border-radius:4px;background:${C.blue}}
.aai-val{width:44px;text-align:right;font-family:"Fira Sans",system-ui,sans-serif;font-weight:600;font-size:15px;font-variant-numeric:tabular-nums}
`

const CURSOR_SVG = (fill, stroke) =>
  `<svg viewBox="0 0 22 30" width="22" height="30"><path d="M2 2 L2 23 L7.5 17.5 L11.5 27 L15 25.5 L11 16.5 L18.5 16.5 Z" fill="${fill}" stroke="${stroke}" stroke-width="1.6" stroke-linejoin="round"/></svg>`

function shapeMarkup(s) {
  if (s.k === 'dot') return `<circle r="${s.s}" fill="${s.c}"/>`
  if (s.k === 'sq')
    return `<rect x="${-s.s / 2}" y="${-s.s / 2}" width="${s.s}" height="${s.s}" rx="2" fill="${s.c}"/>`
  const h = s.s
  return `<path d="M0 ${-h * 0.58} L${h * 0.5} ${h * 0.29} L${-h * 0.5} ${h * 0.29} Z" fill="${s.c}" stroke="${s.c}" stroke-width="2" stroke-linejoin="round"/>`
}
const SPARK =
  'M0 -14 C1.6 -4 4 -1.6 14 0 C4 1.6 1.6 4 0 14 C-1.6 4 -4 1.6 -14 0 C-4 -1.6 -1.6 -4 0 -14Z'

function template() {
  const strip = Array.from(
    { length: 40 },
    (_, i) => `<span>${i % 10}</span>`
  ).join('')
  return `
<div class="aai-stage">
  <div class="aai-bg-modern aai-abs" style="inset:0;position:absolute"></div>
  <div class="aai-bg-retro"></div>

  <div class="aai-win">
    <div class="aai-win-title"><span>Bewerbungen_final_v3.doc</span><span class="aai-win-btns"><i>_</i><i>□</i><i>×</i></span></div>
    <div class="aai-win-menu"><span>Datei</span><span>Bearbeiten</span><span>Ansicht</span><span>Einfügen</span><span>Format</span><span>Extras</span><span>?</span></div>
    <div class="aai-win-page">
      <div class="aai-doc-h">Bewerbung 1 von 247</div>
      ${DOC_LINES.map((w) => `<div class="aai-doc-line" data-w="${w}" style="${w ? '' : 'background:none'}"></div>`).join('')}
      <div class="aai-caret"></div>
    </div>
    <div class="aai-crt"></div>
  </div>

  <svg class="aai-svg" viewBox="0 0 ${W} ${H}">
    <g class="aai-burst">${SHAPES.map((s) => `<g>${shapeMarkup(s)}</g>`).join('')}</g>
    <path class="aai-gA" fill="none" stroke="${C.pink}" stroke-linecap="round" stroke-linejoin="round" opacity="0"/>
    <path class="aai-gB" fill="none" stroke="${C.green}" stroke-linecap="round" stroke-linejoin="round" opacity="0"/>
    <path class="aai-wire" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    <path class="aai-hl" fill="none" stroke="#E6E9EE" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    <g class="aai-rface">
      <g class="aai-reye" data-x="-20" data-y="-52"><circle r="16" fill="#fff" stroke="#222" stroke-width="2.4"/><circle class="aai-pup" r="6.2" fill="#111"/></g>
      <g class="aai-reye" data-x="16" data-y="-58"><circle r="16" fill="#fff" stroke="#222" stroke-width="2.4"/><circle class="aai-pup" r="6.2" fill="#111"/></g>
      <path class="aai-brow" data-cx="-20" data-cy="-76" d="M-33 -73 Q-20 -82 -7 -76" fill="none" stroke="#111" stroke-width="4.5" stroke-linecap="round"/>
      <path class="aai-brow" data-cx="16" data-cy="-82" d="M3 -82 Q16 -89 29 -80" fill="none" stroke="#111" stroke-width="4.5" stroke-linecap="round"/>
    </g>
    <g class="aai-mface">
      <rect class="aai-meye" x="-4.5" y="-8.5" width="9" height="17" rx="4.5" fill="${C.dark}"/>
      <rect class="aai-meye" x="-4.5" y="-8.5" width="9" height="17" rx="4.5" fill="${C.dark}"/>
      <path class="aai-spark1" d="${SPARK}" fill="${C.yellow}"/>
      <path class="aai-spark2" d="${SPARK}" fill="${C.yellow}"/>
    </g>
  </svg>

  <div class="aai-bubble">
    <p>Es sieht so aus, als würden Sie Bewerbungen sichten. Möchten Sie Hilfe?</p>
    <div class="aai-opt"><b></b><span>Ja, bitte helfen</span></div>
    <div class="aai-opt aai-no"><b></b><span>Nein, ohne Hilfe fortfahren</span></div>
    <div class="aai-check"><i></i><span>Diesen Tipp nicht mehr anzeigen</span></div>
    <svg class="aai-bubble-tail" viewBox="0 0 24 26"><path d="M0 0 L18 26 L22 0" fill="#FFFFE1" stroke="#000" stroke-width="1"/><rect x="0.6" y="-2" width="21" height="3" fill="#FFFFE1"/></svg>
  </div>

  <div class="aai-card">
    <div class="aai-head">
      <div class="aai-ava"><svg viewBox="-60 -110 130 210" width="18" height="26"><path d="${toPath(MODERN)}" fill="none" stroke="#fff" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
      <div><div class="aai-head-t">Aivy Assistent</div><div class="aai-head-s"><i></i>Kennt eure Rollen, Profile &amp; Ergebnisse</div></div>
    </div>
    <div class="aai-msg"><span class="aai-typed"></span><div class="aai-dots"><i></i><i></i><i></i></div></div>
    <div class="aai-chips">${CHIPS.map((c) => `<div class="aai-chip">${c}<span class="aai-ripple"></span></div>`).join('')}</div>
    <div class="aai-rows">${ROWS.map(
      (r) => `
      <div class="aai-row">
        <div class="aai-rava" style="background:${r.c}">${r.n
          .split(' ')
          .map((p) => p[0])
          .join('')}</div>
        <div class="aai-rname">${r.n}<small>${r.r}</small></div>
        <div class="aai-track"><div class="aai-fill"></div></div>
        <div class="aai-val">0 %</div>
      </div>`
    ).join('')}
    </div>
  </div>

  <div class="aai-label">
    <span class="aai-odo">${[0, 1, 2, 3].map(() => `<span class="aai-col"><span class="aai-strip">${strip}</span></span>`).join('')}</span>
    <span class="aai-sep"></span>
    <span class="aai-names"><span>Karl Klammer</span><span>Aivy Assistent</span></span>
  </div>

  <div class="aai-cursor aai-cursor-retro">${CURSOR_SVG('#fff', '#000')}</div>
  <div class="aai-cursor aai-cursor-modern">${CURSOR_SVG(C.dark, '#fff')}</div>
</div>`
}

let styleInjected = false
function injectStyle() {
  if (styleInjected || typeof document === 'undefined') return
  const el = document.createElement('style')
  el.setAttribute('data-aivy-assistant-intro', '')
  el.textContent = CSS
  document.head.appendChild(el)
  styleInjected = true
}

function offsetIn(el, root) {
  let x = 0
  let y = 0
  let n = el
  while (n && n !== root) {
    x += n.offsetLeft
    y += n.offsetTop
    n = n.offsetParent
  }
  return { x, y, w: el.offsetWidth, h: el.offsetHeight }
}

/**
 * @param {HTMLElement} container
 * @param {{autoplay?:boolean, loop?:boolean, idle?:boolean, background?:string, delay?:number, onComplete?:()=>void}} [options]
 */
export function createAivyAssistantIntro(container, options = {}) {
  const o = {
    autoplay: true,
    loop: false,
    idle: true,
    background: C.coolWhite,
    delay: 0.2,
    onComplete: null,
    ...options
  }
  injectStyle()

  const root = document.createElement('div')
  root.className = 'aai-root'
  root.innerHTML = template()
  container.appendChild(root)

  const $ = (s) => root.querySelector(s)
  const $$ = (s) => Array.from(root.querySelectorAll(s))
  const stage = $('.aai-stage')
  const el = {
    bgModern: $('.aai-bg-modern'),
    bgRetro: $('.aai-bg-retro'),
    win: $('.aai-win'),
    lines: $$('.aai-doc-line'),
    caret: $('.aai-caret'),
    crt: $('.aai-crt'),
    wire: $('.aai-wire'),
    hl: $('.aai-hl'),
    gA: $('.aai-gA'),
    gB: $('.aai-gB'),
    rface: $('.aai-rface'),
    reyes: $$('.aai-reye'),
    brows: $$('.aai-brow'),
    mface: $('.aai-mface'),
    meyes: $$('.aai-meye'),
    spark1: $('.aai-spark1'),
    spark2: $('.aai-spark2'),
    burst: $$('.aai-burst > g'),
    bubble: $('.aai-bubble'),
    optNo: $('.aai-no'),
    card: $('.aai-card'),
    msg: $('.aai-msg'),
    typed: $('.aai-typed'),
    dots: $$('.aai-dots i'),
    dotsWrap: $('.aai-dots'),
    chips: $$('.aai-chip'),
    ripple: $('.aai-chip .aai-ripple'),
    rows: $$('.aai-row'),
    fills: $$('.aai-fill'),
    vals: $$('.aai-val'),
    cols: $$('.aai-strip'),
    names: $$('.aai-names span'),
    curR: $('.aai-cursor-retro'),
    curM: $('.aai-cursor-modern')
  }
  el.bgModern.style.background = o.background

  // Layout-abhängige Zielpunkte (Stage-Koordinaten)
  let targets = { no: { x: 700, y: 160 }, chip: { x: 470, y: 250 }, caret: [] }
  const measure = () => {
    const no = offsetIn(el.optNo, stage)
    const chip = offsetIn(el.chips[0], stage)
    targets = {
      no: { x: no.x + 60, y: no.y + no.h / 2 },
      chip: { x: chip.x + chip.w * 0.55, y: chip.y + chip.h * 0.55 },
      chipBox: chip
    }
  }
  const fit = () => {
    const s = root.clientWidth / W
    stage.style.transform = `scale(${s})`
  }
  const ro =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null
  ro && ro.observe(root)
  fit()
  measure()
  if (document.fonts && document.fonts.ready)
    document.fonts.ready.then(() => {
      measure()
      render(time)
    })

  let lastTyped = -1

  // ------------------------------------------------------------------
  function render(t) {
    const tr = t < 2.8 ? step(t) : t // Retro-Szene ruckelt bewusst mit 12 fps

    // ---------- Hintergrund-Wipe ----------
    const wipe = E.inOutCubic(prog(t, 2.9, 3.55))
    const R = wipe * 1150
    const mask =
      wipe <= 0
        ? 'none'
        : `radial-gradient(circle at ${RETRO_POS.x}px ${RETRO_POS.y}px, transparent ${R}px, #000 ${R + 0.5}px)`
    el.bgRetro.style.webkitMaskImage = mask
    el.bgRetro.style.maskImage = mask
    el.bgRetro.style.visibility = wipe >= 1 ? 'hidden' : 'visible'

    // ---------- Retro-Fenster ----------
    const winIn = prog(tr, 0, 0.25)
    const crtY = E.inCubic(prog(t, 2.55, 2.74))
    const crtX = E.inCubic(prog(t, 2.72, 2.88))
    const winS = lerp(0.7, 1, winIn)
    el.win.style.opacity = winIn > 0 && crtX < 1 ? 1 : 0
    el.win.style.transform = `scale(${winS * lerp(1, 0.004, crtX)}, ${winS * lerp(1, 0.006, crtY)})`
    el.crt.style.opacity = clamp(crtY * 1.6)
    // Tipp-Zeilen
    const typeT = prog(tr, 0.3, 1.9) * DOC_LINES.length
    let caretLine = 0
    el.lines.forEach((ln, i) => {
      const f = clamp(typeT - i)
      ln.style.width = `${DOC_LINES[i] * f}%`
      if (typeT > i) caretLine = i
    })
    const cl = el.lines[caretLine]
    el.caret.style.left = `${cl.offsetLeft + cl.offsetWidth + 3}px`
    el.caret.style.top = `${cl.offsetTop - 3}px`
    el.caret.style.opacity = Math.floor(t * 2.5) % 2 === 0 ? 1 : 0

    // ---------- Retro-Clip-Transform ----------
    const pop =
      tr < 0.45
        ? 0
        : tr < 0.62
          ? lerp(0, 1.15, prog(tr, 0.45, 0.62))
          : lerp(1.15, 1, prog(tr, 0.62, 0.75))
    const rock = ((4 * Math.PI) / 180) * Math.sin(tr * 2 * Math.PI * 0.9)
    const sad = prog(tr, 2.35, 2.5)
    const retroT = {
      x: RETRO_POS.x,
      y: RETRO_POS.y + sad * 6,
      s: RETRO_POS.s * pop,
      r: rock
    }

    // Glitch-Jitter
    const glitchOn = t > 2.45 && t < 2.82
    const gf = Math.floor(t * 24)
    const jit = glitchOn ? (rnd(gf) - 0.5) * 10 : 0
    retroT.x += jit

    // ---------- Modern-Transform ----------
    const popT = t - 4.62
    const mPop =
      popT > 0 ? 1 + 0.13 * Math.exp(-5 * popT) * Math.sin(15 * popT) : 1
    const bob =
      t > 4.62 ? Math.sin((t - 4.62) * 2.1) * 5 * clamp((t - 4.62) * 2) : 0
    const modernT = {
      x: MODERN_POS.x,
      y: MODERN_POS.y + bob,
      s: MODERN_POS.s * mPop,
      r: 0
    }

    // ---------- Draht-Morph ----------
    const uA = prog(t, 2.78, 3.55)
    const uB = prog(t, 3.85, 4.65)
    const retroPts = xf(RETRO, retroT)
    const modernPts = xf(MODERN, modernT)
    const twAmp =
      30 *
      Math.exp(-5.5 * Math.max(0, t - 3.55)) *
      prog(t, 3.45, 3.6) *
      (1 - uB)
    const tw = Math.sin((t - 3.5) * 2 * Math.PI * 7)
    const pts = new Array(N)
    const stA = 0.45
    const stB = 0.4
    for (let i = 0; i < N; i++) {
      const s = i / (N - 1)
      const lx = LINE.x - LINE.len / 2 + s * LINE.len
      const ly =
        LINE.y +
        twAmp * Math.sin(s * Math.PI) * tw +
        twAmp *
          0.35 *
          Math.sin(s * Math.PI * 3) *
          Math.cos((t - 3.5) * 2 * Math.PI * 11)
      const pa = E.inOutCubic(clamp((uA - s * stA) / (1 - stA)))
      const pb = E.inOutCubic(clamp((uB - s * stB) / (1 - stB)))
      let x = lerp(retroPts[i][0], lx, pa)
      let y = lerp(retroPts[i][1], ly, pa)
      x = lerp(x, modernPts[i][0], pb)
      y = lerp(y, modernPts[i][1], pb)
      pts[i] = [x, y]
    }
    const d = toPath(pts)
    const wireVisible = pop > 0
    el.wire.setAttribute('d', d)
    el.wire.style.opacity = wireVisible ? 1 : 0
    el.wire.setAttribute(
      'stroke',
      mixColor(C.wire, C.blue, E.inOutCubic(prog(uA, 0.15, 0.85)))
    )
    el.wire.setAttribute(
      'stroke-width',
      lerp(lerp(8.5 * pop, 12, E.outCubic(uA)), 16, E.outCubic(uB)).toFixed(2)
    )
    // Highlight (Metallglanz) nur retro
    const hlO = wireVisible ? 1 - prog(t, 2.7, 2.85) : 0
    el.hl.style.opacity = hlO
    if (hlO > 0)
      el.hl.setAttribute(
        'd',
        toPath(xf(RETRO, { ...retroT, x: retroT.x - 1.6, y: retroT.y - 1.6 }))
      )
    // Glitch-Kopien
    const gShow = glitchOn && rnd(gf + 3) > 0.35
    ;[el.gA, el.gB].forEach((g, k) => {
      g.style.opacity = gShow ? 1 : 0
      if (gShow) {
        const off = (k ? 1 : -1) * (3 + rnd(gf + k) * 6)
        g.setAttribute('d', toPath(xf(RETRO, { ...retroT, x: retroT.x + off })))
        g.setAttribute('stroke-width', 8)
      }
    })

    // ---------- Retro-Gesicht ----------
    const faceOut = prog(t, 2.62, 2.76)
    const faceS = pop * (1 - E.inCubic(faceOut))
    el.rface.style.opacity = faceS > 0.01 ? 1 : 0
    el.rface.setAttribute(
      'transform',
      `translate(${retroT.x} ${retroT.y}) rotate(${(retroT.r * 180) / Math.PI}) scale(${RETRO_POS.s * faceS})`
    )
    // Blick
    let look = [0, 0]
    if (tr > 0.75) look = [-4, 3] // aufs Dokument
    if (tr > 1.05) look = [2, -4] // zur Sprechblase
    if (tr > 1.65) look = [-3, 3] // zum Cursor
    if (tr > 2.0) look = [-2, -2]
    if (tr > 2.35) look = [0, 4] // traurig
    const blinkR = (tr > 1.5 && tr < 1.62) || (tr > 2.42 && tr < 2.5) ? 0.12 : 1
    el.reyes.forEach((g) => {
      g.setAttribute(
        'transform',
        `translate(${g.dataset.x} ${g.dataset.y}) scale(1 ${blinkR})`
      )
      g.querySelector('.aai-pup').setAttribute(
        'transform',
        `translate(${look[0]} ${look[1]})`
      )
    })
    const raise = tr > 0.95 && tr < 1.35 ? -7 : tr > 1.35 && tr < 2.3 ? -3 : 0
    el.brows.forEach((b, i) => {
      const rot = sad * (i === 0 ? -16 : 16)
      b.setAttribute(
        'transform',
        `translate(0 ${raise + sad * 3}) rotate(${rot} ${b.dataset.cx} ${b.dataset.cy})`
      )
    })

    // ---------- Retro-Sprechblase ----------
    const bIn = tr >= 1.0 && tr < 2.33
    const bS = tr < 1.0 ? 0 : tr < 1.09 ? 0.6 : 1
    el.bubble.style.opacity = bIn ? 1 : 0
    el.bubble.style.transform = `scale(${bS})`
    el.optNo.classList.toggle('hover', t > 2.02 && t < 2.2)
    el.optNo.classList.toggle('down', t >= 2.2)

    // ---------- Retro-Cursor ----------
    const cm = E.inOutCubic(prog(t, 1.45, 2.0))
    const cx = lerp(560, targets.no.x, cm)
    const cy = lerp(520, targets.no.y, cm)
    const cOut = prog(t, 2.45, 2.6)
    el.curR.style.opacity = t > 1.4 ? 1 - cOut : 0
    el.curR.style.transform = `translate(${cx}px, ${cy}px) scale(${t > 2.18 && t < 2.3 ? 0.85 : 1})`

    // ---------- Label / Odometer ----------
    const FROM = [1, 9, 9, 7]
    const TO = [2, 20, 22, 26]
    el.cols.forEach((c, i) => {
      const p = E.inOutCubic(prog(t, 2.95 + i * 0.08, 3.65 + i * 0.12))
      c.style.transform = `translateY(${-lerp(FROM[i], TO[i], p) * 20}px)`
    })
    const nm = E.inOutCubic(prog(t, 3.25, 3.6))
    el.names[0].style.transform = `translateY(${-nm * 20}px)`
    el.names[0].style.opacity = 1 - nm
    el.names[1].style.transform = `translateY(${(1 - nm) * 20}px)`
    el.names[1].style.opacity = nm

    // ---------- Burst-Shapes ----------
    el.burst.forEach((g, i) => {
      const s = SHAPES[i]
      const tau = t - 4.6 - i * 0.04
      if (tau <= 0) {
        g.style.opacity = 0
        return
      }
      const p = spring(tau, 6, 11)
      const fl = Math.sin(t * 1.3 + i * 1.7) * 4
      const x = lerp(MODERN_POS.x, s.x, p)
      const y = lerp(MODERN_POS.y, s.y, p) + fl * clamp(tau)
      const sc =
        clamp(tau * 6) * (1 + 0.25 * Math.exp(-4 * tau) * Math.sin(12 * tau))
      const rot = (s.r || 0) + (1 - clamp(p)) * 160 + Math.sin(t * 0.9 + i) * 6
      g.style.opacity = 1
      g.setAttribute(
        'transform',
        `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${rot.toFixed(1)}) scale(${sc.toFixed(3)})`
      )
    })

    // ---------- Modernes Gesicht ----------
    const eyeOpen = spring(t - 4.66, 9, 16)
    let mBlink = 1
    const blinkAt = (bt) => {
      const d = Math.abs(t - bt)
      if (d < 0.09) mBlink = Math.min(mBlink, 0.1 + d * 10)
    }
    ;[6.3, 8.95].forEach(blinkAt)
    if (t > DURATION) {
      const ph = (t - DURATION) % 4.2
      if (ph < 0.18) blinkAt(t - ph + 0.09)
    }
    const lookX = t > 5.0 ? lerp(0, 3.5, E.outCubic(prog(t, 5.0, 5.3))) : 0
    const lookY = t > 8.4 ? lerp(0, 2, E.outCubic(prog(t, 8.4, 8.7))) : 0
    el.mface.style.opacity = t > 4.62 ? 1 : 0
    el.mface.setAttribute(
      'transform',
      `translate(${modernT.x} ${modernT.y}) scale(${modernT.s})`
    )
    ;[
      [-2, -24],
      [18, -24]
    ].forEach(([ex, ey], i) => {
      el.meyes[i].setAttribute(
        'transform',
        `translate(${ex + lookX} ${ey + lookY}) scale(1 ${Math.max(0, eyeOpen * mBlink).toFixed(3)})`
      )
    })
    const sp1 = spring(t - 4.85, 8, 14)
    const sp2 = spring(t - 5.0, 8, 14)
    const tw1 = 1 + 0.12 * Math.sin(t * 3.1)
    const tw2 = 1 + 0.15 * Math.sin(t * 3.7 + 1)
    el.spark1.setAttribute(
      'transform',
      `translate(54 -84) rotate(${(1 - sp1) * -120}) scale(${(sp1 * tw1 * 0.9).toFixed(3)})`
    )
    el.spark2.setAttribute(
      'transform',
      `translate(72 -60) rotate(${(1 - sp2) * -120}) scale(${(sp2 * tw2 * 0.48).toFixed(3)})`
    )

    // ---------- Chat-Karte ----------
    const cIn = spring(t - 4.85, 7, 10)
    el.card.style.opacity = clamp((t - 4.85) * 4)
    el.card.style.transform = `translateX(${(1 - cIn) * 70}px)`
    const mIn = E.outBack(prog(t, 5.3, 5.6))
    el.msg.style.opacity = clamp((t - 5.3) * 6)
    el.msg.style.transform = `scale(${lerp(0.85, 1, mIn)})`
    const dotsOn = t > 5.3 && t < 6.05
    el.dotsWrap.style.opacity = dotsOn ? 1 : 0
    el.dots.forEach((dt, i) => {
      dt.style.transform = `translateY(${-Math.max(0, Math.sin(t * 7 - i * 0.9)) * 4}px)`
    })
    const nChars = Math.round(prog(t, 6.05, 7.25) * MSG_LEN)
    if (nChars !== lastTyped) {
      lastTyped = nChars
      let left = nChars
      let vis = ''
      let ghost = ''
      for (const m of MSG) {
        const take = Math.min(left, m.t.length)
        const a = m.t.slice(0, take)
        const b = m.t.slice(take)
        left -= take
        if (a) vis += m.b ? `<b>${a}</b>` : a
        if (b) ghost += m.b ? `<b>${b}</b>` : b
      }
      const caret =
        nChars > 0 && nChars < MSG_LEN ? `<span class="aai-tcaret"></span>` : ''
      el.typed.innerHTML = `${vis}${caret}<span class="aai-ghost">${ghost}</span>`
    }
    el.chips.forEach((ch, i) => {
      const p = E.outBack(prog(t, 7.3 + i * 0.1, 7.6 + i * 0.1))
      ch.style.opacity = clamp((t - 7.3 - i * 0.1) * 5)
      ch.style.transform = `translateY(${(1 - p) * 10}px)`
    })
    el.chips[0].classList.toggle('on', t >= 8.32)
    const rp = prog(t, 8.3, 8.75)
    el.ripple.style.opacity = rp > 0 && rp < 1 ? (1 - rp) * 0.55 : 0
    if (targets.chipBox) {
      const s = 1 + rp * 30
      el.ripple.style.transform = `translate(${targets.chip.x - targets.chipBox.x - 5}px, ${targets.chip.y - targets.chipBox.y - 5}px) scale(${s})`
    }
    el.rows.forEach((r, i) => {
      const a = 8.55 + i * 0.16
      const p = E.outCubic(prog(t, a, a + 0.4))
      r.style.opacity = p
      r.style.transform = `translateY(${(1 - p) * 12}px)`
      const f = E.outExpo(prog(t, a + 0.1, a + 1.0))
      el.fills[i].style.width = `${ROWS[i].v * f}%`
      el.vals[i].textContent = `${Math.round(ROWS[i].v * f)} %`
    })

    // ---------- Moderner Cursor ----------
    const mm = E.inOutCubic(prog(t, 7.75, 8.25))
    const mx = lerp(900, targets.chip.x, mm)
    const my = lerp(560, targets.chip.y, mm)
    const mOut = prog(t, 8.9, 9.2)
    el.curM.style.opacity = t > 7.7 ? 1 - mOut : 0
    el.curM.style.transform = `translate(${mx + mOut * 20}px, ${my + mOut * 30}px) scale(${t > 8.25 && t < 8.38 ? 0.82 : 1})`
  }

  // ---------- Playback ----------
  let time = 0
  let playing = false
  let raf = 0
  let last = 0
  let completed = false
  let visible = true
  const reduce =
    typeof matchMedia !== 'undefined' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches

  const tick = (now) => {
    raf = 0
    if (!playing) return
    const dt = Math.min(0.1, (now - last) / 1000)
    last = now
    time += dt
    const local = time - o.delay
    if (local >= DURATION && !completed) {
      completed = true
      o.onComplete && o.onComplete()
    }
    if (o.loop && local >= DURATION + 2.5) {
      time = 0
      completed = false
      lastTyped = -1
    }
    if (!o.loop && !o.idle && local >= DURATION) {
      render(DURATION)
      playing = false
      return
    }
    render(Math.max(0, time - o.delay))
    if (visible) raf = requestAnimationFrame(tick)
  }
  const play = () => {
    if (playing) return
    playing = true
    last = performance.now()
    if (visible) raf = requestAnimationFrame(tick)
  }
  const pause = () => {
    playing = false
    cancelAnimationFrame(raf)
    raf = 0
  }
  const seek = (t) => {
    time = t + o.delay
    completed = t >= DURATION
    render(Math.max(0, t))
  }
  const replay = () => {
    completed = false
    lastTyped = -1
    seek(0)
    time = 0
    play()
  }

  const io =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => {
          visible = e.isIntersecting
          if (visible && playing && !raf) {
            last = performance.now()
            raf = requestAnimationFrame(tick)
          }
        })
      : null
  io && io.observe(root)

  if (reduce) {
    seek(DURATION)
  } else {
    render(0)
    if (o.autoplay) play()
  }

  return {
    play,
    pause,
    seek,
    replay,
    get time() {
      return Math.max(0, time - o.delay)
    },
    get playing() {
      return playing
    },
    duration: DURATION,
    setBackground(bg) {
      el.bgModern.style.background = bg
    },
    destroy() {
      pause()
      ro && ro.disconnect()
      io && io.disconnect()
      root.remove()
    }
  }
}
